import React from 'react';
import {BrokerReportShareRecord} from '../../types/BrokerReportShareRecord';
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {DateUtility} from "../common/DateUtility";

interface BrokerReportSharingRecordsPanelProps {
    open: boolean;
    shareRecords: BrokerReportShareRecord[];
    onClose: () => void;
}

export let BrokerReportSharingRecordsPanel = function (props: BrokerReportSharingRecordsPanelProps) {
    const panelRef = React.useRef<HTMLDivElement>(null);

    // The admin header scrolls with the page, so the open panel tracks the header's bottom edge until the header leaves the viewport.
    React.useLayoutEffect(
        function () {
            if (!props.open) {
                return;
            }

            function alignPanelTopWithAdminHeader() {
                let panel = panelRef.current;
                let adminHeader = document.querySelector('.admin-header');
                if (!panel || !adminHeader) {
                    return;
                }

                let adminHeaderBottom = adminHeader.getBoundingClientRect().bottom;
                panel.style.top = `${Math.max(0, adminHeaderBottom)}px`;
            }

            alignPanelTopWithAdminHeader();
            window.addEventListener('scroll', alignPanelTopWithAdminHeader);
            window.addEventListener('resize', alignPanelTopWithAdminHeader);
            return function () {
                window.removeEventListener('scroll', alignPanelTopWithAdminHeader);
                window.removeEventListener('resize', alignPanelTopWithAdminHeader);
            };
        },
        [props.open]
    );

    function handleClickCloseOption() {
        // The closed panel is inert rather than merely invisible, so a click that lands on it does nothing.
        if (!props.open) {
            return;
        }

        props.onClose();
    }

    function entryDetailText(shareRecord: BrokerReportShareRecord) {
        let detailText = `${DateUtility.dbDateToSharingRecordDateTime(shareRecord.created_at)} by ${shareRecord.sender_user?.person?.name || ''}`;

        if (shareRecord.recipient) {
            detailText += ` to ${shareRecord.recipient}`;
        }

        return detailText;
    }

    return (
        <div ref={panelRef} className={`broker-report-sharing-records-panel${props.open ? ' open' : ''}`}>
            <div className="broker-report-sharing-records-panel-header">
                <img
                    className="broker-report-sharing-records-panel-header-icon"
                    src="/static/images/status-records-icon.svg"
                />
                <div className="broker-report-sharing-records-panel-header-text">Sharing Records</div>
                <div
                    className="broker-report-sharing-records-panel-close-option"
                    onClick={handleClickCloseOption}
                >
                    <img
                        className="broker-report-sharing-records-panel-close-option-icon"
                        src="/static/images/close-light-gray.svg"
                    />
                </div>
            </div>
            <div className="broker-report-sharing-records-panel-entries">
                {
                    // The server returns the records newest first, so they are rendered in the order they arrive.
                    props.shareRecords.map(function (shareRecord) {
                        return (
                            <div key={shareRecord.id} className="broker-report-sharing-records-panel-entry">
                                <div className="broker-report-sharing-records-panel-entry-pill">
                                    {shareRecord.share_type_name}
                                </div>
                                <div className="broker-report-sharing-records-panel-entry-detail">
                                    {entryDetailText(shareRecord)}
                                </div>
                                <ConditionalRenderer
                                    if={shareRecord.note}
                                    renderer={() => (
                                        <div className="broker-report-sharing-records-panel-entry-note">
                                            {shareRecord.note}
                                        </div>
                                    )}
                                />
                            </div>
                        );
                    })
                }
            </div>
        </div>
    );
};
